import { useState, useEffect, useCallback } from "react";
import { Users, UserCheck, UserX, Clock, Shield, RefreshCw, CheckCircle, XCircle, MessageSquare, Send, Trash2, Mail, ChevronDown, ChevronUp, Crown, Gift, AlertTriangle, Database, Download, RotateCcw, Globe, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { fetchAdminStats, fetchPendingUsers, fetchAllUsers, approveUser, rejectUser, toggleUserAdmin, toggleMasterAdmin, fetchAllMessages, replyToMessage, markMessageRead, deleteMessage, grantComplimentaryAccess, revokeComplimentaryAccess, grantLegacyAccess, fetchBackups, createBackup, deleteBackup, restoreBackup, fetchWebhookStatus } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/contexts/ThemeContext";
import ThemeToggle from "@/components/ThemeToggle";

const PRIMARY_ADMIN_EMAIL = "francis18santiago@gmail.com";

// Confirmation Dialog Component
function ConfirmMasterAdminDialog({ isOpen, user, onConfirm, onCancel, isGranting, isDark }) {
  if (!isOpen || !user) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className={`rounded-xl border p-6 max-w-md mx-4 shadow-2xl ${
        isDark ? 'bg-slate-800 border-amber-500/30' : 'bg-white border-amber-200'
      }`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`flex items-center justify-center w-12 h-12 rounded-full border ${
            isDark ? 'bg-amber-500/20 border-amber-500/30' : 'bg-amber-50 border-amber-200'
          }`}>
            <Crown size={24} className={isDark ? 'text-amber-400' : 'text-amber-600'} />
          </div>
          <div>
            <h3 className={`text-lg font-bold ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>
              {isGranting ? "Grant Master Admin Access" : "Revoke Master Admin Access"}
            </h3>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{user.email}</p>
          </div>
        </div>
        
        <div className={`rounded-lg border p-4 mb-6 ${
          isDark ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200'
        }`}>
          <p className={`text-sm font-medium mb-2 ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
            {isGranting ? "⚠️ Warning: This grants full administrative privileges" : "⚠️ Warning: This removes master admin privileges"}
          </p>
          <ul className={`text-xs space-y-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
            {isGranting ? (
              <>
                <li>• Can grant/revoke admin status to other users</li>
                <li>• Has equivalent access to the primary admin</li>
                <li>• Only you (primary admin) can revoke this access</li>
              </>
            ) : (
              <>
                <li>• User will retain regular admin status</li>
                <li>• Will no longer be able to manage other admins</li>
              </>
            )}
          </ul>
        </div>
        
        <p className={`text-sm mb-6 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
          Are you sure you want to {isGranting ? "grant" : "revoke"} master admin access {isGranting ? "to" : "from"} <strong className={isDark ? 'text-amber-400' : 'text-amber-600'}>{user.email}</strong>?
        </p>
        
        <div className="flex gap-3 justify-end">
          <Button
            onClick={onCancel}
            variant="outline"
            className={isDark 
              ? "border-slate-600 text-slate-300 hover:bg-slate-700"
              : "border-gray-300 text-gray-600 hover:bg-gray-100"
            }
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className={isGranting 
              ? "bg-amber-600 hover:bg-amber-500 text-white font-semibold"
              : "bg-red-600 hover:bg-red-500 text-white font-semibold"
            }
          >
            <Crown size={16} className="mr-2" />
            {isGranting ? "Grant Master Admin" : "Revoke Master Admin"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isPrimaryAdmin = user?.email?.toLowerCase() === PRIMARY_ADMIN_EMAIL.toLowerCase();
  
  const [stats, setStats] = useState({ total_users: 0, pending_users: 0, approved_users: 0, unread_messages: 0 });
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [viewMode, setViewMode] = useState("pending"); // "pending", "all", "admins", "messages", "system"
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const [expandedMessage, setExpandedMessage] = useState(null);
  
  // System tab state
  const [backups, setBackups] = useState([]);
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [systemLoading, setSystemLoading] = useState(false);
  
  // Master admin confirmation dialog state
  const [masterAdminConfirm, setMasterAdminConfirm] = useState({ isOpen: false, user: null, isGranting: false });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsData, pendingData, allData, messagesData] = await Promise.all([
        fetchAdminStats(),
        fetchPendingUsers(),
        fetchAllUsers(),
        fetchAllMessages()
      ]);
      setStats(statsData);
      setPendingUsers(pendingData);
      setAllUsers(allData);
      setMessages(messagesData);
    } catch (err) {
      toast.error("Failed to load admin data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleApprove = async (userId, email) => {
    setActionLoading(userId);
    try {
      await approveUser(userId);
      toast.success(`Approved ${email}`);
      await loadData();
    } catch (err) {
      toast.error("Failed to approve user");
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to reject and remove ${email}? This will delete their account and all data.`)) {
      return;
    }
    setActionLoading(userId);
    try {
      await rejectUser(userId);
      toast.success(`Rejected and removed ${email}`);
      await loadData();
    } catch (err) {
      toast.error("Failed to reject user");
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleAdmin = async (userId, email, currentIsAdmin) => {
    const action = currentIsAdmin ? "remove admin status from" : "grant admin status to";
    if (!window.confirm(`Are you sure you want to ${action} ${email}?`)) {
      return;
    }
    setActionLoading(userId);
    try {
      const result = await toggleUserAdmin(userId);
      toast.success(result.message);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update admin status");
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  // Open master admin confirmation dialog
  const openMasterAdminConfirm = (targetUser, isGranting) => {
    setMasterAdminConfirm({ isOpen: true, user: targetUser, isGranting });
  };

  // Handle master admin toggle after confirmation
  const handleConfirmMasterAdmin = async () => {
    const { user: targetUser } = masterAdminConfirm;
    if (!targetUser) return;
    
    setMasterAdminConfirm({ isOpen: false, user: null, isGranting: false });
    setActionLoading(targetUser.id);
    
    try {
      const result = await toggleMasterAdmin(targetUser.id);
      toast.success(result.message);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update master admin status");
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendReply = async (messageId) => {
    if (!replyContent.trim()) {
      toast.error("Please enter a reply");
      return;
    }
    setActionLoading(messageId);
    try {
      await replyToMessage(messageId, replyContent);
      toast.success("Reply sent");
      setReplyingTo(null);
      setReplyContent("");
      await loadData();
    } catch (err) {
      toast.error("Failed to send reply");
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) {
      return;
    }
    setActionLoading(messageId);
    try {
      await deleteMessage(messageId);
      toast.success("Message deleted");
      await loadData();
    } catch (err) {
      toast.error("Failed to delete message");
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkRead = async (messageId) => {
    try {
      await markMessageRead(messageId);
      await loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleGrantComplimentary = async (userId, email) => {
    setActionLoading(userId);
    try {
      await grantComplimentaryAccess(userId);
      toast.success(`Free access granted to ${email}`);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to grant free access");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevokeComplimentary = async (userId, email) => {
    if (!window.confirm(`Remove free access from ${email}? They will need a subscription to continue.`)) return;
    setActionLoading(userId);
    try {
      await revokeComplimentaryAccess(userId);
      toast.success(`Free access revoked from ${email}`);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke free access");
    } finally {
      setActionLoading(null);
    }
  };

  const handleGrantLegacyAccess = async () => {
    if (!window.confirm("Grant free indefinite access to all legacy accounts (those without security questions)? This cannot be easily undone.")) return;
    setActionLoading("legacy");
    try {
      const result = await grantLegacyAccess();
      toast.success(result.message);
      await loadData();
    } catch (err) {
      toast.error("Failed to grant legacy access");
    } finally {
      setActionLoading(null);
    }
  };

  // System tab handlers
  const loadSystemData = useCallback(async () => {
    setSystemLoading(true);
    try {
      const [backupsData, webhookData] = await Promise.all([
        fetchBackups(),
        fetchWebhookStatus()
      ]);
      setBackups(backupsData.backups || []);
      setWebhookStatus(webhookData);
    } catch (err) {
      console.error("Failed to load system data:", err);
    } finally {
      setSystemLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewMode === "system") loadSystemData();
  }, [viewMode, loadSystemData]);

  const handleCreateBackup = async () => {
    setActionLoading("backup");
    try {
      const result = await createBackup();
      toast.success(`Backup created: ${result.backup_name} (${result.size_mb} MB)`);
      await loadSystemData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create backup");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteBackup = async (name) => {
    if (!window.confirm(`Delete backup "${name}"? This cannot be undone.`)) return;
    setActionLoading(name);
    try {
      await deleteBackup(name);
      toast.success(`Backup "${name}" deleted`);
      await loadSystemData();
    } catch (err) {
      toast.error("Failed to delete backup");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestoreBackup = async (name) => {
    if (!window.confirm(`⚠️ RESTORE from "${name}"?\n\nThis will OVERWRITE all current data. A safety backup will be created first.\n\nAre you absolutely sure?`)) return;
    setActionLoading(name);
    try {
      const result = await restoreBackup(name);
      toast.success(result.message);
      await loadSystemData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  };

  return (
    <div className="pb-32">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Shield className="h-8 w-8 text-amber-400" />
            <h1 className={`text-3xl md:text-4xl font-bold tracking-tight ${isDark ? 'text-slate-100' : 'text-gray-900'}`} style={{ fontFamily: "'Manrope', sans-serif" }}>
              Admin Panel
            </h1>
          </div>
          <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>Manage user accounts, approvals, and messages</p>
        </div>
        <ThemeToggle />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className={`rounded-xl border p-4 ${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Users size={16} className="text-blue-400" />
            <span className={`text-xs uppercase tracking-widest font-semibold ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Total Users</span>
          </div>
          <p className={`text-2xl font-mono font-bold ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>{stats.total_users}</p>
        </div>
        <div className={`rounded-xl border p-4 ${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Clock size={16} className={isDark ? 'text-amber-400' : 'text-amber-500'} />
            <span className={`text-xs uppercase tracking-widest font-semibold ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Pending</span>
          </div>
          <p className={`text-2xl font-mono font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{stats.pending_users}</p>
        </div>
        <div className={`rounded-xl border p-4 ${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            <UserCheck size={16} className="text-emerald-400" />
            <span className={`text-xs uppercase tracking-widest font-semibold ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Approved</span>
          </div>
          <p className={`text-2xl font-mono font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{stats.approved_users}</p>
        </div>
        <div className={`rounded-xl border p-4 ${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare size={16} className="text-blue-400" />
            <span className={`text-xs uppercase tracking-widest font-semibold ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Unread Messages</span>
          </div>
          <p className={`text-2xl font-mono font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{stats.unread_messages}</p>
        </div>
      </div>

      {/* View Toggle & Refresh */}
      <div className="flex items-center justify-between mb-4">
        <div className={`flex items-center gap-1 rounded-lg p-1 ${isDark ? 'bg-slate-800/60' : 'bg-gray-100'}`}>
          <button
            onClick={() => setViewMode("all")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
              viewMode === "all" 
                ? isDark ? "bg-slate-700/80 text-slate-100" : "bg-white text-gray-900 shadow-sm"
                : isDark ? "text-slate-500 hover:text-slate-300" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Users size={13} />
            All Users
          </button>
          <button
            onClick={() => setViewMode("pending")}
            className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
              viewMode === "pending" 
                ? isDark ? "bg-slate-700/80 text-slate-100" : "bg-white text-gray-900 shadow-sm"
                : isDark ? "text-slate-500 hover:text-slate-300" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Clock size={13} />
            Pending
            {pendingUsers.length > 0 && (
              <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-amber-500 text-white rounded-full">
                {pendingUsers.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setViewMode("messages")}
            className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
              viewMode === "messages" 
                ? isDark ? "bg-slate-700/80 text-slate-100" : "bg-white text-gray-900 shadow-sm"
                : isDark ? "text-slate-500 hover:text-slate-300" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <MessageSquare size={13} />
            Messages
            {stats.unread_messages > 0 && (
              <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-blue-500 text-white rounded-full">
                {stats.unread_messages}
              </span>
            )}
          </button>
          <button
            onClick={() => setViewMode("admins")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
              viewMode === "admins" 
                ? "bg-purple-500/20 text-purple-300" 
                : "text-purple-400/70 hover:text-purple-300"
            }`}
          >
            <Shield size={13} />
            Admins ({allUsers.filter(u => u.is_admin).length})
          </button>
          {isPrimaryAdmin && (
            <button
              onClick={() => setViewMode("system")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                viewMode === "system" 
                  ? isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-50 text-emerald-700 shadow-sm"
                  : isDark ? "text-emerald-400/70 hover:text-emerald-300" : "text-emerald-500 hover:text-emerald-700"
              }`}
            >
              <Database size={13} />
              System
            </button>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadData}
          disabled={loading}
          className={isDark 
            ? "border-slate-700/50 hover:bg-slate-800 text-slate-300 hover:text-slate-100"
            : "border-gray-300 hover:bg-gray-100 text-gray-600 hover:text-gray-900"
          }
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          <span className="ml-1.5">Refresh</span>
        </Button>
      </div>

      {/* Content based on view mode */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-6 w-6 border-2 border-blue-400 border-t-transparent rounded-full" />
        </div>
      ) : viewMode === "pending" ? (
        /* Pending Users View */
        pendingUsers.length === 0 ? (
          <div className="text-center py-12">
            <UserCheck size={48} className={`mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-400'}`} />
            <p className={isDark ? 'text-slate-500' : 'text-gray-500'}>No pending approvals</p>
            <p className={`text-xs mt-1 ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>New user signups will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingUsers.map((user) => (
              <div
                key={user.id}
                data-testid={`pending-user-${user.id}`}
                className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                  isDark ? 'bg-slate-800/50 border-amber-500/20' : 'bg-amber-50 border-amber-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border ${
                    isDark ? 'bg-amber-500/15 border-amber-500/20' : 'bg-amber-100 border-amber-300'
                  }`}>
                    <Clock size={18} className={isDark ? 'text-amber-400' : 'text-amber-600'} />
                  </div>
                  <div>
                    <p className={`font-medium ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>{user.email}</p>
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Registered: {formatDate(user.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    data-testid={`approve-btn-${user.id}`}
                    onClick={() => handleApprove(user.id, user.email)}
                    disabled={actionLoading === user.id}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                  >
                    {actionLoading === user.id ? (
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                      <>
                        <CheckCircle size={16} className="mr-1.5" />
                        Approve
                      </>
                    )}
                  </Button>
                  <Button
                    data-testid={`reject-btn-${user.id}`}
                    onClick={() => handleReject(user.id, user.email)}
                    disabled={actionLoading === user.id}
                    variant="outline"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                  >
                    <XCircle size={16} className="mr-1.5" />
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : viewMode === "all" ? (
        /* All Users View */
        <div className="space-y-2">
          {/* Legacy accounts banner */}
          {(() => {
            const legacyCount = allUsers.filter(u => !u.security_question && !u.is_admin && !u.is_master_admin && !u.complimentary_access).length;
            if (legacyCount === 0) return null;
            return (
              <div className={`rounded-xl border p-4 mb-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-300'}`}>
                <div className="flex items-start gap-3">
                  <AlertTriangle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className={`text-sm font-semibold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                      {legacyCount} Legacy Account{legacyCount !== 1 ? 's' : ''} Without Free Access
                    </p>
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-amber-400/70' : 'text-amber-600'}`}>
                      These accounts were created before security questions were required. Grant them free access to unblock their access.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleGrantLegacyAccess}
                  disabled={actionLoading === "legacy"}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold flex-shrink-0"
                  data-testid="grant-legacy-access-btn"
                >
                  {actionLoading === "legacy" ? (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <><Gift size={14} className="mr-1.5" />Grant All Free Access</>
                  )}
                </Button>
              </div>
            );
          })()}
          {allUsers.map((listUser) => {
            const isListUserPrimaryAdmin = listUser.email === PRIMARY_ADMIN_EMAIL;
            return (
              <div
                key={listUser.id}
                data-testid={`user-row-${listUser.id}`}
                className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                  isDark 
                    ? `bg-slate-800/50 ${listUser.is_admin ? "border-purple-500/30" : listUser.complimentary_access ? "border-emerald-500/30" : listUser.is_approved ? "border-slate-700/50" : "border-amber-500/20"}`
                    : `${listUser.is_admin ? "bg-purple-50 border-purple-200" : listUser.complimentary_access ? "bg-emerald-50 border-emerald-200" : listUser.is_approved ? "bg-white border-gray-200" : "bg-amber-50 border-amber-200"}`
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border ${
                    listUser.is_admin 
                      ? isDark ? "bg-purple-500/15 border-purple-500/20" : "bg-purple-100 border-purple-300"
                      : listUser.complimentary_access
                        ? isDark ? "bg-emerald-500/15 border-emerald-500/20" : "bg-emerald-100 border-emerald-300"
                        : listUser.is_approved 
                          ? isDark ? "bg-emerald-500/15 border-emerald-500/20" : "bg-emerald-100 border-emerald-300"
                          : isDark ? "bg-amber-500/15 border-amber-500/20" : "bg-amber-100 border-amber-300"
                  }`}>
                    {listUser.is_admin ? (
                      <Shield size={18} className="text-purple-400" />
                    ) : listUser.complimentary_access ? (
                      <Gift size={18} className="text-emerald-400" />
                    ) : listUser.is_approved ? (
                      <UserCheck size={18} className="text-emerald-400" />
                    ) : (
                      <Clock size={18} className={isDark ? "text-amber-400" : "text-amber-600"} />
                    )}
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <p className={`font-medium ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>{listUser.email}</p>
                      {listUser.is_admin && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">Admin</span>
                      )}
                      {isListUserPrimaryAdmin && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">Primary</span>
                      )}
                      {listUser.complimentary_access && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">Free Access</span>
                      )}
                      {!listUser.security_question && !listUser.is_admin && !listUser.is_master_admin && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">Legacy</span>
                      )}
                    </div>
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                      {listUser.is_approved ? "Approved" : "Pending"} • Joined {formatDate(listUser.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Grant / Revoke Free Access */}
                  {!isListUserPrimaryAdmin && !listUser.is_admin && (
                    <Button
                      onClick={() => listUser.complimentary_access
                        ? handleRevokeComplimentary(listUser.id, listUser.email)
                        : handleGrantComplimentary(listUser.id, listUser.email)
                      }
                      disabled={actionLoading === listUser.id}
                      size="sm"
                      variant="outline"
                      data-testid={listUser.complimentary_access ? `revoke-free-${listUser.id}` : `grant-free-${listUser.id}`}
                      className={listUser.complimentary_access
                        ? "border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                        : "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300"
                      }
                    >
                      <Gift size={14} className="mr-1" />
                      {actionLoading === listUser.id ? "..." : listUser.complimentary_access ? "Revoke Free Access" : "Grant Free Access"}
                    </Button>
                  )}
                  {/* Admin toggle - only primary admin can change admin status */}
                  {isPrimaryAdmin && listUser.is_approved && !isListUserPrimaryAdmin && (
                    <Button
                      onClick={() => handleToggleAdmin(listUser.id, listUser.email, listUser.is_admin)}
                      disabled={actionLoading === listUser.id}
                      size="sm"
                      variant="outline"
                      className={listUser.is_admin 
                        ? "border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300" 
                        : "border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:text-purple-300"
                      }
                    >
                      <Shield size={14} className="mr-1" />
                      {listUser.is_admin ? "Remove Admin" : "Make Admin"}
                    </Button>
                  )}
                  {/* Approve/Reject for pending users */}
                  {!listUser.is_approved && (
                    <>
                      <Button
                        onClick={() => handleApprove(listUser.id, listUser.email)}
                        disabled={actionLoading === listUser.id}
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                      >
                        <CheckCircle size={14} className="mr-1" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleReject(listUser.id, listUser.email)}
                        disabled={actionLoading === listUser.id}
                        size="sm"
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                      >
                        <XCircle size={14} className="mr-1" />
                        Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : viewMode === "admins" ? (
        /* Admins View */
        (() => {
          const adminUsers = allUsers.filter(u => u.is_admin);
          return adminUsers.length === 0 ? (
            <div className="text-center py-12">
              <Shield size={48} className={`mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-400'}`} />
              <p className={isDark ? 'text-slate-500' : 'text-gray-500'}>No admin users</p>
              <p className={`text-xs mt-1 ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>Grant admin status to users from the &quot;All Users&quot; tab</p>
            </div>
          ) : (
            <div className="space-y-2">
              {adminUsers.map((adminUser) => {
                const isAdminUserPrimary = adminUser.email === PRIMARY_ADMIN_EMAIL;
                return (
                  <div
                    key={adminUser.id}
                    className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                      isDark ? 'bg-slate-800/50 border-purple-500/30' : 'bg-purple-50 border-purple-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`flex items-center justify-center w-10 h-10 rounded-full border ${
                        isDark ? 'bg-purple-500/15 border-purple-500/20' : 'bg-purple-100 border-purple-300'
                      }`}>
                        <Shield size={18} className="text-purple-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className={`font-medium ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>{adminUser.email}</p>
                          <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">
                            Admin
                          </span>
                          {isAdminUserPrimary && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                              Primary
                            </span>
                          )}
                          {adminUser.is_master_admin && !isAdminUserPrimary && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                              Master
                            </span>
                          )}
                        </div>
                        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Joined {formatDate(adminUser.created_at)}</p>
                      </div>
                    </div>
                    {/* Only primary admin can manage admin/master admin status */}
                    {isPrimaryAdmin && !isAdminUserPrimary && (
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => openMasterAdminConfirm(adminUser, !adminUser.is_master_admin)}
                          disabled={actionLoading === adminUser.id}
                          size="sm"
                          variant="outline"
                          className={adminUser.is_master_admin 
                            ? "border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300" 
                            : "border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
                          }
                        >
                          <Crown size={14} className="mr-1" />
                          {adminUser.is_master_admin ? "Revoke Master" : "Grant Master"}
                        </Button>
                        <Button
                          onClick={() => handleToggleAdmin(adminUser.id, adminUser.email, true)}
                          disabled={actionLoading === adminUser.id}
                          size="sm"
                          variant="outline"
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                        >
                          <Shield size={14} className="mr-1" />
                          Remove Admin
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()
      ) : viewMode === "system" ? (
        /* System Tab - Backups & Webhook */
        <div className="space-y-6">
          {/* Webhook Status */}
          <div className={`rounded-xl border p-5 ${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Globe size={18} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
                <h3 className={`text-sm font-semibold uppercase tracking-wider ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Stripe Webhook</h3>
              </div>
              <Button variant="outline" size="sm" onClick={loadSystemData} disabled={systemLoading}
                className={isDark ? "border-slate-700/50 text-slate-400 hover:text-slate-200" : "border-gray-300 text-gray-500"}>
                <RefreshCw size={12} className={systemLoading ? "animate-spin" : ""} />
              </Button>
            </div>
            {webhookStatus ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  {webhookStatus.webhook_secret_configured ? (
                    <Wifi size={14} className="text-emerald-400" />
                  ) : (
                    <WifiOff size={14} className="text-red-400" />
                  )}
                  <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    Secret: {webhookStatus.webhook_secret_configured ? "Configured" : "Missing"}
                  </span>
                </div>
                {webhookStatus.registered_endpoints?.map((ep, i) => (
                  <div key={i} className={`rounded-lg border p-3 ${isDark ? 'bg-slate-900/50 border-slate-700/30' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-block w-2 h-2 rounded-full ${ep.status === 'enabled' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                      <span className={`text-xs font-semibold uppercase ${ep.status === 'enabled' ? 'text-emerald-400' : 'text-red-400'}`}>{ep.status}</span>
                    </div>
                    <p className={`text-sm font-mono break-all ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{ep.url}</p>
                    {ep.url !== webhookStatus.expected_url && (
                      <p className="text-xs text-amber-400 mt-1">Expected: {webhookStatus.expected_url}</p>
                    )}
                  </div>
                ))}
                {(!webhookStatus.registered_endpoints || webhookStatus.registered_endpoints.length === 0) && (
                  <p className={`text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}>No webhook endpoints registered in Stripe</p>
                )}
              </div>
            ) : (
              <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Loading...</p>
            )}
          </div>

          {/* Database Backups */}
          <div className={`rounded-xl border p-5 ${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Database size={18} className={isDark ? 'text-emerald-400' : 'text-emerald-600'} />
                <h3 className={`text-sm font-semibold uppercase tracking-wider ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Database Backups</h3>
              </div>
              <Button
                onClick={handleCreateBackup}
                disabled={actionLoading === "backup"}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                data-testid="create-backup-btn"
              >
                {actionLoading === "backup" ? (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <><Download size={14} className="mr-1.5" />Create Backup</>
                )}
              </Button>
            </div>
            <p className={`text-xs mb-4 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
              Backups run automatically every 24 hours (keeps last 7). You can also create manual backups.
            </p>
            {backups.length === 0 ? (
              <div className="text-center py-6">
                <Database size={32} className={`mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-gray-400'}`} />
                <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>No backups yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {backups.map((bk) => (
                  <div key={bk.name} data-testid={`backup-${bk.name}`}
                    className={`rounded-lg border p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 ${
                      isDark ? 'bg-slate-900/50 border-slate-700/30' : 'bg-gray-50 border-gray-200'
                    }`}>
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-800'}`}>{bk.name}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                        {formatDate(bk.created_at)} · {bk.size_mb} MB · {bk.file_count} files
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {isPrimaryAdmin && (
                        <Button
                          onClick={() => handleRestoreBackup(bk.name)}
                          disabled={actionLoading === bk.name}
                          size="sm"
                          variant="outline"
                          className={isDark
                            ? "border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                            : "border-amber-300 text-amber-600 hover:bg-amber-50"
                          }
                        >
                          <RotateCcw size={12} className="mr-1" />
                          Restore
                        </Button>
                      )}
                      <Button
                        onClick={() => handleDeleteBackup(bk.name)}
                        disabled={actionLoading === bk.name}
                        size="sm"
                        variant="ghost"
                        className={isDark ? "text-slate-500 hover:text-red-400" : "text-gray-400 hover:text-red-600"}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Messages View */
        messages.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare size={48} className={`mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-400'}`} />
            <p className={isDark ? 'text-slate-500' : 'text-gray-500'}>No messages yet</p>
            <p className={`text-xs mt-1 ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>User messages will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`rounded-xl border overflow-hidden ${
                  isDark 
                    ? `bg-slate-800/50 ${msg.read ? "border-slate-700/50" : "border-blue-500/30"}`
                    : `${msg.read ? "bg-white border-gray-200" : "bg-blue-50 border-blue-200"}`
                }`}
              >
                <div 
                  className={`p-4 cursor-pointer transition-colors ${
                    isDark ? 'hover:bg-slate-700/20' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    setExpandedMessage(expandedMessage === msg.id ? null : msg.id);
                    if (!msg.read) handleMarkRead(msg.id);
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                        isDark 
                          ? msg.read ? "bg-slate-700/50" : "bg-blue-500/15 border border-blue-500/20"
                          : msg.read ? "bg-gray-100" : "bg-blue-100 border border-blue-200"
                      }`}>
                        <Mail size={18} className={msg.read ? (isDark ? "text-slate-500" : "text-gray-400") : "text-blue-400"} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className={`font-medium ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>{msg.user_email}</p>
                          {!msg.read && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-blue-500/20 text-blue-300 border border-blue-500/30">
                              New
                            </span>
                          )}
                          {msg.replies?.length > 0 && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                              Replied
                            </span>
                          )}
                        </div>
                        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{formatDate(msg.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={(e) => { e.stopPropagation(); handleDeleteMessage(msg.id); }}
                        disabled={actionLoading === msg.id}
                        size="sm"
                        variant="ghost"
                        className={isDark 
                          ? "text-slate-500 hover:text-red-400 hover:bg-red-500/10"
                          : "text-gray-400 hover:text-red-600 hover:bg-red-50"
                        }
                      >
                        <Trash2 size={14} />
                      </Button>
                      {expandedMessage === msg.id ? <ChevronUp size={18} className={isDark ? "text-slate-500" : "text-gray-400"} /> : <ChevronDown size={18} className={isDark ? "text-slate-500" : "text-gray-400"} />}
                    </div>
                  </div>
                  <p className={`text-sm mt-3 line-clamp-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{msg.content}</p>
                </div>

                {/* Expanded content */}
                {expandedMessage === msg.id && (
                  <div className={`border-t p-4 ${
                    isDark ? 'border-slate-700/50 bg-slate-900/30' : 'border-gray-200 bg-gray-50'
                  }`}>
                    <p className={`text-sm whitespace-pre-wrap mb-4 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{msg.content}</p>
                    
                    {/* Previous replies */}
                    {msg.replies?.length > 0 && (
                      <div className="space-y-3 mb-4">
                        {msg.replies.map((reply) => (
                          <div key={reply.id} className="pl-4 border-l-2 border-emerald-500/30">
                            <div className="flex items-center gap-2 mb-1">
                              <Shield size={12} className="text-emerald-400" />
                              <span className="text-xs font-semibold text-emerald-400">{reply.admin_email}</span>
                              <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{formatDate(reply.created_at)}</span>
                            </div>
                            <p className={`text-sm whitespace-pre-wrap ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{reply.content}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Reply form */}
                    {replyingTo === msg.id ? (
                      <div className="space-y-3">
                        <Textarea
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder="Type your reply..."
                          maxLength={1000}
                          className={`min-h-[100px] ${
                            isDark 
                              ? 'bg-slate-800/50 border-slate-700/50 focus:border-emerald-500/50 text-slate-200 placeholder:text-slate-600'
                              : 'bg-white border-gray-300 focus:border-emerald-500 text-gray-900 placeholder:text-gray-400'
                          }`}
                        />
                        <div className="flex items-center justify-between">
                          <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{replyContent.length}/1000</span>
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => { setReplyingTo(null); setReplyContent(""); }}
                              size="sm"
                              variant="outline"
                              className={isDark 
                                ? "border-slate-700/50 text-slate-400"
                                : "border-gray-300 text-gray-600"
                              }
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => handleSendReply(msg.id)}
                              disabled={actionLoading === msg.id || !replyContent.trim()}
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                            >
                              <Send size={14} className="mr-1" />
                              Send Reply
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Button
                        onClick={() => setReplyingTo(msg.id)}
                        size="sm"
                        variant="outline"
                        className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                      >
                        <Send size={14} className="mr-1" />
                        Reply
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}
      
      {/* Master Admin Confirmation Dialog */}
      <ConfirmMasterAdminDialog
        isOpen={masterAdminConfirm.isOpen}
        user={masterAdminConfirm.user}
        isGranting={masterAdminConfirm.isGranting}
        onConfirm={handleConfirmMasterAdmin}
        onCancel={() => setMasterAdminConfirm({ isOpen: false, user: null, isGranting: false })}
        isDark={isDark}
      />
    </div>
  );
}
